import React, { useState, useMemo, useEffect } from "react";
import { observer } from "mobx-react";
import Pagination from "@mui/material/Pagination";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { useStore } from "../../../../../hooks";
import {
  CardContainer,
  ScrollableCards,
  PaginationContainer,
  AgentListWrapper,
  PageSizeContainer,
  DisplayRecordsWrapper,
  NoAgentsContainer,
} from "./AgentTabs.styled";
import ModalAgentsDetail from "../../components/ModalAgentsDetail/ModalAgentsDetail";
import LogoAnn from "../../../../../elements/icon/assets/logo_ann_customer.svg";
import AgentCard from "../../../AgentsList/components/AgentCard";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useMediaQueries } from "../../../../../utils/mediaQueries";
import Spinner from "../../../../../components/common/Spinner";
import { Icon } from "../../../../../elements/icon";
import ShareAgentModal from "../ShareAgentModal/ShareAgentModal";
import ConfirmationModal from "../../../../../elements/v2/modals/ConfirmationModal";
import SuccessModal from "../../../../../elements/v2/SuccessModal/SuccessModal";
import Alerts from "../../../../../elements/v2/Alerts/Alerts";
import { AgentDeletionMessages } from "../../../../../utils/enums";

const AgentsTabs = observer(() => {
  const navigate = useNavigate();
  const { agentStore, authStore } = useStore();
  const { viewAgentStore } = agentStore;
  const { isMobile, isTablet } = useMediaQueries();
  const hasStatusFilter =
    viewAgentStore.statusFilter && viewAgentStore.statusFilter.length > 0;
  const hasScopeFilter =
    viewAgentStore.scopeFilter && viewAgentStore.scopeFilter.length > 0;
  const hasModifiedFilter =
    viewAgentStore.modifiedFilter && viewAgentStore.modifiedFilter.length > 0;
  const hasCategoryFilter =
    viewAgentStore.categoryFilter && viewAgentStore.categoryFilter.length > 0;
  const hasOwnerFilter =
    viewAgentStore.ownerFilter && viewAgentStore.ownerFilter.length > 0;
  const [ownersMap, setOwnersMap] = useState({});

  useEffect(() => {
    const fetchOwners = async () => {
      if (!authStore.users.length) {
        await authStore.fetchAllUsers();
      }
      const map = {};
      agentStore.agents.forEach((agent) => {
        const user = authStore.users.find((u) => u.id === agent.created_by);
        if (user) {
          map[agent.id] = `${user.first_name} ${user.last_name}`.trim();
        } else {
          map[agent.id] = "System";
        }
      });
      setOwnersMap(map);
    };
    fetchOwners();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentStore.agents, authStore.users]);

  const isWithinDateRange = (modifiedDate, filterOption) => {
    const now = dayjs();
    const lastModified = dayjs(modifiedDate);
    const dateFilters = {
      "last 7 days": () => now.subtract(7, "day"),
      "last 30 days": () => now.subtract(30, "day"),
      "last 90 days": () => now.subtract(90, "day"),
      "this year": () => now.startOf("year"),
    };

    return dateFilters[filterOption]
      ? lastModified.isAfter(dateFilters[filterOption]())
      : true;
  };

  const agentsFiltered = useMemo(() => {
    let filteredAgents = agentStore.agents.filter(
      (assistant) => assistant.provider === "openai"
    );

    if (viewAgentStore.searchValue) {
      filteredAgents = filteredAgents.filter((assistant) =>
        assistant.name
          .toLowerCase()
          .includes(viewAgentStore.searchValue.toLowerCase())
      );
    }

    if (hasStatusFilter) {
      filteredAgents = filteredAgents.filter((assistant) =>
        viewAgentStore.statusFilter.includes(assistant.status)
      );
    }

    if (hasScopeFilter) {
      filteredAgents = filteredAgents.filter((assistant) =>
        viewAgentStore.scopeFilter.includes(assistant.scope)
      );
    }

    if (hasModifiedFilter) {
      filteredAgents = filteredAgents.filter((assistant) =>
        viewAgentStore.modifiedFilter.some((filterOption) =>
          isWithinDateRange(assistant.last_modified, filterOption.toLowerCase())
        )
      );
    }

    if (hasCategoryFilter) {
      filteredAgents = filteredAgents.filter((assistant) =>
        viewAgentStore.categoryFilter.includes(assistant.category)
      );
    }

    if (hasOwnerFilter) {
      filteredAgents = filteredAgents.filter((assistant) => {
        const includesMyAgents =
          viewAgentStore.ownerFilter.includes("my-agents");
        const isMyAgent = assistant.created_by === authStore.userId;
        const isOtherOwnerAgent = viewAgentStore.ownerFilter.some(
          (ownerId) =>
            ownerId !== "my-agents" && assistant.created_by === ownerId
        );
        return (includesMyAgents && isMyAgent) || isOtherOwnerAgent;
      });
    }

    return filteredAgents;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    agentStore.agents.length,
    viewAgentStore.searchValue,
    viewAgentStore.statusFilter,
    viewAgentStore.scopeFilter,
    viewAgentStore.modifiedFilter,
    viewAgentStore.categoryFilter,
    viewAgentStore.ownerFilter,
    authStore.userId,
  ]);

  const totalPages = Math.ceil(
    agentsFiltered.length / viewAgentStore.itemsPerPage
  );

  const currentAgents = useMemo(() => {
    const startIndex =
      (viewAgentStore.currentPage - 1) * viewAgentStore.itemsPerPage;
    const endIndex = startIndex + viewAgentStore.itemsPerPage;
    return agentsFiltered.slice(startIndex, endIndex);
  }, [agentsFiltered, viewAgentStore.currentPage, viewAgentStore.itemsPerPage]);

  const [isModalOpen, setModalOpen] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successTitle, setSuccessTitle] = useState("");
  const [deleteAlert, setDeleteAlert] = useState({
    isOpen: false,
    severity: "",
    title: "",
    message: "",
  });
  const [selectedActionAgent, setSelectedActionAgent] = useState(null);
  const [showCreatingModal, setShowCreatingModal] = useState(false);
  const [showDuplicatingModal, setShowDuplicatingModal] = useState(false);

  useEffect(() => {
    if (!agentStore.isDuplicating && showCreatingModal) {
      setShowCreatingModal(false);
    }
    if (!agentStore.isDuplicating && showDuplicatingModal) {
      setShowDuplicatingModal(false);
      setSuccessMessage("Agent duplicated successfully.");
      setShowSuccessModal(true);
      setSuccessTitle("Success");
    }
  }, [agentStore.isDuplicating, showCreatingModal, showDuplicatingModal]);

  const handleOpenModal = (e, assistant) => {
    e.stopPropagation();
    const starters = assistant.conversation_starters.filter(
      (starter) => starter
    );
    const content = {
      id: assistant.id,
      image: assistant?.photo_url || LogoAnn,
      name: assistant.name,
      type: "Agent",
      description1: assistant.job_title,
      description2: assistant.prompt,
      conversations: 4,
      members: 1,
      starters: [],
      knowledgeBase: assistant.knowledge_base.map((filePath) =>
        filePath.replace("s3://", "").split("/").splice(-1).pop()
      ),
      conversation_starters: starters || [],
      visibility: "draft",
      actions: assistant.action || [],
      original: assistant,
      typeAgent: assistant.type,
      prompt_id: assistant.prompt_id || "",
    };
    agentStore.onSelectedAgent(content);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleEdit = (assistant) => {
    if (
      assistant.type === "langchain" &&
      assistant.prompt_id &&
      !["", "empty"].includes(assistant.prompt_id)
    ) {
      // Navigate to specific prompt management page
      navigate(
        `/v2/prompt-management-system/${assistant.prompt_id.split("#")[1]}`
      );
    } else {
      // Navigate to regular playground
      navigate({
        pathname: "/v2/assistants/playground",
        search: `agent=${assistant.id}&customer=${assistant.customer_id}`,
      });
    }
  };

  const handleUse = (assistant) => {
    navigate({
      pathname: "/home",
      search: `agent=${assistant.id}&customer=${assistant.customer_id}`,
    });
  };

  const handleShare = (agentId, customerId) => {
    setSelectedActionAgent({ agentId, customerId });
    setShowShareModal(true);
  };
  
  const handleDuplicate = (agentId, customerId) => {
    const agent = agentStore.agents.find((a) => a.id === agentId);
    if (agent) {
      agentStore.onSelectedAgent({ original: agent });
    }
    setSelectedActionAgent({ agentId, customerId });
    setShowDuplicateModal(true);
  };
  
  const handleRemove = async (agentId, customerId) => {
    if (!authStore.userData) {
      await authStore.fetchUserData(authStore.userId);
    }
    if (!(authStore.isGroupSuperAdmin || authStore.isGroupAdmin)) {
      setDeleteAlert({
        isOpen: true,
        severity: "error",
        title: "Permission Error",
        message: AgentDeletionMessages.PERMISSION_DENIED,
      });
      return;
    }
    setSelectedActionAgent({ agentId, customerId });
    setShowDeleteModal(true);
  };
  const confirmDuplicate = () => {
    setShowDuplicateModal(false);
    setShowDuplicatingModal(true);
    agentStore.duplicateAgent();
  };
  const confirmRemove = async () => {
    setShowDeleteModal(false);
    try {
      await agentStore.viewAgentStore.handleConfirmDelete({
        id: selectedActionAgent.agentId,
        customer_id: selectedActionAgent.customerId,
      });
      await agentStore.fetchGetAgent();
      setSuccessMessage("Agent removed successfully.");
      setShowSuccessModal(true);
    } catch (error) {
      setDeleteAlert({
        isOpen: true,
        severity: "error",
        title: "Deletion Error",
        message: AgentDeletionMessages.DEFAULT,
      });
    }
  };

  return (
    <>
      <AgentListWrapper>
        <p>Agents {`(${agentsFiltered.length})`}</p>
      </AgentListWrapper>
      {agentStore.isLoading && <Spinner />}
      {!agentStore.isLoading &&
        (agentsFiltered.length > 0 ? (
          <ScrollableCards>
            <CardContainer isMobile={isMobile}>
              {currentAgents.map((assistant, index) => (
                <AgentCard
                  key={index}
                  avatar={assistant?.photo_url || LogoAnn}
                  title={assistant.name}
                  description={assistant.prompt || "..."}
                  updatedBy={"System"}
                  lastModified={assistant.last_modified}
                  onClick={(e) => handleOpenModal(e, assistant)}
                  onEdit={() => handleEdit(assistant)}
                  onUse={() => handleUse(assistant)}
                  type={assistant.type}
                  prompt_id={assistant.prompt_id}
                  category={
                    assistant.category
                      ? assistant.category.charAt(0).toUpperCase() +
                        assistant.category.slice(1)
                      : undefined
                  }
                  status={assistant.active ? "Active" : "Draft"}
                  owner={ownersMap[assistant.id] || "System"}
                  agentId={assistant.id}
                  customerId={assistant.customer_id}
                  onShare={handleShare}
                  onRemove={handleRemove}
                  onDuplicate={handleDuplicate}
                />
              ))}
            </CardContainer>
          </ScrollableCards>
        ) : (
          <NoAgentsContainer>
            <p>No Agents Found</p>
            <p>
              Your filters returned no matches. Try adjusting or resetting them
            </p>
            <Icon name={"noAgents"} width={"90px"} height={"88px"} />
          </NoAgentsContainer>
        ))}
      {agentStore.selectedAgent && (
        <ModalAgentsDetail open={isModalOpen} onClose={handleCloseModal} />
      )}
      {/* Modales globales para acciones */}
      <ShareAgentModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        agentId={selectedActionAgent?.agentId}
      />
      <ConfirmationModal
        isOpen={showDuplicateModal}
        title="Duplicate"
        message="Caution: You are about to duplicate the agent. Are you sure you want to continue?"
        icon="duplicate"
        onClose={() => setShowDuplicateModal(false)}
        onConfirm={confirmDuplicate}
      />
      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Are you sure you want to delete?"
        message="Deleting this agent is permanent and cannot be undone. Do you want to proceed?"
        icon="deleteOutline"
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmRemove}
      />
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successTitle || "Success"}
        message={successMessage}
      />
      {deleteAlert.isOpen && (
        <Alerts
          severity={deleteAlert.severity}
          width={"auto"}
          top={"61px"}
          right={"16px"}
          title={deleteAlert.title}
          message={deleteAlert.message}
          onClose={() => setDeleteAlert({ ...deleteAlert, isOpen: false })}
        />
      )}
      {totalPages > 0 && (
        <PaginationContainer isMobile={isMobile} isTablet={isTablet}>
          <PageSizeContainer>
            <>
              <span>Show</span>
              <Select
                value={viewAgentStore.itemsPerPage}
                onChange={(e) =>
                  viewAgentStore.onChangeItemsPerPage(e.target.value)
                }
                size="small"
                sx={{
                  mx: 1,
                  minWidth: 80,
                  height: "32px",
                }}
              >
                {viewAgentStore.availablePageSizes.map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
              <span>Entries</span>
            </>
            <DisplayRecordsWrapper>
              Displaying {viewAgentStore.currentPage} through{" "}
              {agentsFiltered.length -
                viewAgentStore.itemsPerPage *
                  (viewAgentStore.currentPage - 1)}{" "}
              out of {agentsFiltered.length} Records
            </DisplayRecordsWrapper>
          </PageSizeContainer>

          <Pagination
            count={totalPages}
            page={viewAgentStore.currentPage}
            onChange={viewAgentStore.onChangePage}
            color="primary"
            size="large"
            variant="outlined"
            shape="rounded"
            showFirstButton
            showLastButton
            defaultPage={1}
            siblingCount={0}
            boundaryCount={0}
          />
        </PaginationContainer>
      )}
      <ConfirmationModal
        isOpen={showCreatingModal}
        title="Creating agent"
        message="This may take a few minutes. Please check back later to continue."
        icon="info"
        displayCancel={false}
        onConfirm={() => setShowCreatingModal(false)}
        onClose={() => setShowCreatingModal(false)}
      />
      <ConfirmationModal
        isOpen={showDuplicatingModal}
        title="Duplicating agent"
        message="This may take a few minutes. Please check back later to continue."
        icon="info"
        displayCancel={false}
        onConfirm={() => setShowDuplicatingModal(false)}
        onClose={() => setShowDuplicatingModal(false)}
      />
    </>
  );
});

export default AgentsTabs;
